import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";

function Navbar({ user, setUser }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Add shadow on scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [menuOpen]);

  const handleLogout = () => {
    localStorage.removeItem("loggedInUser");
    setUser(null);
    setMenuOpen(false);
    navigate("/login");
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
  };

  const isHappeningsActive = ["/blog", "/news", "/events"].includes(
    location.pathname
  );

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    {
      label: "Happenings",
      dropdown: [
        { to: "/blog", label: "Blogs" },
        { to: "/news", label: "News Room" },
        { to: "/events", label: "Events" },
      ],
    },
    { to: "/members", label: "Members" },
    { to: "/gallery", label: "Gallery" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
      <div className="nav-container">
        {/* Logo */}
        <div className="logo">
          <Link to="/" onClick={closeMenu}>
            <img src="images/logo.png" alt="AlumniConnect Logo" />
          </Link>
        </div>

        {/* Desktop Nav Links */}
        <ul className="nav-links nav-links--desktop">
          {navLinks.map((item) => (
            <li key={item.label}>
              {item.dropdown ? (
                <div
                  className={`dropdown ${
                    isHappeningsActive ? "dropdown--active" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="dropdown-toggle"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    {item.label}
                    <span
                      className={`dropdown-arrow ${
                        dropdownOpen ? "dropdown-arrow--open" : ""
                      }`}
                    >
                      ▼
                    </span>
                    <span className="nav-underline" />
                  </button>

                  <div
                    className={`dropdown-menu ${
                      dropdownOpen ? "dropdown-menu--open" : ""
                    }`}
                  >
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.to}
                        to={subItem.to}
                        className={
                          location.pathname === subItem.to ? "active" : ""
                        }
                        onClick={() => setDropdownOpen(false)}
                      >
                        {subItem.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  to={item.to}
                  className={location.pathname === item.to ? "active" : ""}
                >
                  {item.label}
                  <span className="nav-underline" />
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* Desktop Buttons */}
        <div className="nav-buttons nav-buttons--desktop">
          {user ? (
            <>
              <Link to="/dashboard" className="btn btn--outline">
                <span className="btn-icon">👤</span>
                Hello, {user.name.split(" ")[0]}
              </Link>

              <button onClick={handleLogout} className="btn btn--primary">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn--outline">
                Login
              </Link>

              <Link to="/alumni-register" className="btn btn--primary">
                Register
              </Link>
            </>
          )}
        </div>

        {/* Hamburger */}
        <button
          className={`hamburger ${menuOpen ? "hamburger--open" : ""}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* Mobile Overlay */}
      <div
        className={`nav-overlay ${menuOpen ? "nav-overlay--visible" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* Mobile Drawer */}
      <div className={`nav-drawer ${menuOpen ? "nav-drawer--open" : ""}`}>
        <div className="nav-drawer__header">
          <span className="nav-drawer__title">Menu</span>
        </div>

        <ul className="nav-drawer__links">
          {navLinks.map((item, i) => (
            <React.Fragment key={item.label}>
              {!item.dropdown ? (
                <li
                  className="nav-drawer__item"
                  style={{
                    animationDelay: menuOpen ? `${i * 50}ms` : "0ms",
                  }}
                >
                  <Link
                    to={item.to}
                    className={`nav-drawer__link ${
                      location.pathname === item.to
                        ? "nav-drawer__link--active"
                        : ""
                    }`}
                    onClick={closeMenu}
                  >
                    <span className="nav-drawer__link-text">
                      {item.label}
                    </span>

                    <span className="nav-drawer__arrow">›</span>
                  </Link>
                </li>
              ) : (
                <li
                  className="nav-drawer__item"
                  style={{
                    animationDelay: menuOpen ? `${i * 50}ms` : "0ms",
                  }}
                >
                  <div className="mobile-dropdown-title">
                    {item.label}
                  </div>

                  <div className="mobile-sub-links">
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.to}
                        to={subItem.to}
                        className={`nav-drawer__link nav-drawer__sublink ${
                          location.pathname === subItem.to
                            ? "nav-drawer__link--active"
                            : ""
                        }`}
                        onClick={closeMenu}
                      >
                        <span className="nav-drawer__link-text">
                          {subItem.label}
                        </span>

                        <span className="nav-drawer__arrow">›</span>
                      </Link>
                    ))}
                  </div>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>

        <div className="nav-drawer__footer">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="btn btn--outline btn--full"
                onClick={closeMenu}
              >
                <span className="btn-icon">👤</span>
                Hello, {user.name.split(" ")[0]}
              </Link>

              <button
                onClick={handleLogout}
                className="btn btn--primary btn--full"
                type="button"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="btn btn--outline btn--full"
                onClick={closeMenu}
              >
                Login
              </Link>

              <Link
                to="/alumni-register"
                className="btn btn--primary btn--full"
                onClick={closeMenu}
              >
                Register
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;